import { useCallback, useState } from 'react'
import { AiInsightsModal } from '../components/ai/AiInsightsModal'
import { analyzeExpandedHistory } from '../lib/analysis-utils'
import { generateAiInsights } from '../services/ai' // Points to new client service
import type { AnalysisResult, MarketData, UserSettings } from '../types'

export const useAIInsights = () => {
    const [isOpen, setIsOpen] = useState(false)
    const [response, setResponse] = useState<string | null>(null)
    const [isGenerating, setIsGenerating] = useState(false)

    const generateInsights = useCallback(
        async (
            analysis: AnalysisResult | null,
            marketData: MarketData | null,
            settings: UserSettings,
            pageContext: 'Home' | 'Dashboard' | 'Crossover' | 'Variance' | 'StrikeInspector' | 'Scanner' = 'Dashboard',
            extraData?: any,
            intradayHistory: any[] = [],
            signals?: any[], // Added signals argument
        ) => {
            setIsGenerating(true)
            setIsOpen(true) // Open modal immediately
            setResponse(null) // Clear previous

            if (!analysis || !marketData) {
                setResponse('Insufficient data to generate detailed institutional insights.')
                setIsGenerating(false)
                return
            }

            try {
                // Calculate History Summary Locally
                const historySummary = analyzeExpandedHistory(
                    intradayHistory,
                    settings.symbol,
                    Number(settings.range),
                    settings.mode,
                    marketData.spotPrice,
                )

                // Extract Key Options Data
                const resOpt = marketData.options.find((o) => o.strike === analysis.resistanceStrike)
                const supOpt = marketData.options.find((o) => o.strike === analysis.supportStrike)

                const contextPayload = {
                    market: {
                        spotPrice: marketData.spotPrice,
                        vix: marketData.vix || 0,
                        timestamp: marketData.timestamp,
                    },
                    analysis: {
                        shift: analysis.shift,
                        resistanceStrike: analysis.resistanceStrike,
                        supportStrike: analysis.supportStrike,
                        variance: analysis.variance,
                        support2: analysis.supportStrike2,
                        resistance2: analysis.resistanceStrike2,
                    },
                    keyLevels: {
                        resCeOI: resOpt ? resOpt.callOpenInterest : 0,
                        resCeChg: resOpt ? resOpt.callChangePercent : 0,
                        supPeOI: supOpt ? supOpt.putOpenInterest : 0,
                        supPeChg: supOpt ? supOpt.putChangePercent : 0,
                    },
                    historySummary,
                    signals: signals || [], // Pass signals to AI
                }

                const payload = {
                    symbol: settings.symbol,
                    mode: settings.mode.toString(),
                    range: Number(settings.range),
                    activePage: pageContext,
                    extraData,
                    context: contextPayload,
                }

                // Use Client Service Function
                const data = await generateAiInsights({ data: payload })

                if (data.success) {
                    setResponse(data.ideas)
                } else {
                    throw new Error(data.error)
                }
            } catch (error: any) {
                console.error('AI API Error:', error)
                const errorMessage = `Failed to generate insights: ${error.message}`
                setResponse(errorMessage)
            } finally {
                setIsGenerating(false)
            }
        },
        [],
    )

    const closeAiModal = () => {
        setIsOpen(false)
        setResponse(null)
    }

    return {
        isOpen,
        response,
        isGenerating,
        generateInsights,
        closeAiModal,
        AiModalComponent: AiInsightsModal,
    }
}
